/**
 * SVG Stroke Animation Library
 * Универсальная библиотека для анимации stroke в SVG элементах
 *
 * Data атрибуты:
 * data-stroke-animate="true" - активирует анимацию для SVG
 * data-stroke-duration="1000" - длительность анимации в мс (default: 1000)
 * data-stroke-delay="0" - задержка перед стартом в мс (default: 0)
 * data-stroke-reverse="false" - реверс анимации (default: false)
 * data-stroke-random-direction="false" - случайное направление для каждого path (default: false)
 * data-stroke-mode="sequential" - режим: "sequential" или "simultaneous" (default: sequential)
 * data-stroke-stagger="100" - задержка между элементами в sequential режиме в мс (default: 100)
 * data-stroke-easing="ease-in-out" - CSS easing функция (default: ease-in-out)
 * data-stroke-auto-start="true" - автостарт при загрузке (default: true)
 * data-stroke-loop="false" - зациклить анимацию (default: false)
 * data-stroke-loop-delay="0" - задержка перед повтором цикла в мс (default: 0)
 * data-stroke-observer="true" - использовать Intersection Observer (default: false)
 * data-stroke-threshold="0.3" - процент видимости для срабатывания (0-1) (default: 0.3)
 * data-stroke-observer-once="true" - сработать только один раз (default: true)
 *
 * Классы:
 * .stroke-initialized - добавляется при инициализации
 * .stroke-animating - добавляется во время анимации
 * .stroke-completed - добавляется после завершения анимации
 */

class StrokeAnimator {
  constructor(svg, options = {}) {
    this.svg = svg;
    this.paths = Array.from(svg.querySelectorAll('path, line, polyline, polygon, circle, ellipse, rect'));
    this.isAnimating = false;
    this.animationFrames = [];
    this.observer = null;
    this.hasAnimated = false;
    this.pathDirections = new Map(); // Хранит направление для каждого пути

    // Получаем опции из data-атрибутов или параметров
    this.options = {
      duration: parseInt(svg.dataset.strokeDuration) || options.duration || 1000,
      delay: parseInt(svg.dataset.strokeDelay) || options.delay || 0,
      reverse: svg.dataset.strokeReverse === 'true' || options.reverse || false,
      randomDirection: svg.dataset.strokeRandomDirection === 'true' || options.randomDirection || false,
      mode: svg.dataset.strokeMode || options.mode || 'sequential',
      stagger: parseInt(svg.dataset.strokeStagger) || options.stagger || 100,
      easing: svg.dataset.strokeEasing || options.easing || 'ease-in-out',
      autoStart: svg.dataset.strokeAutoStart !== 'false' && (options.autoStart !== false),
      loop: svg.dataset.strokeLoop === 'true' || options.loop || false,
      loopDelay: parseInt(svg.dataset.strokeLoopDelay) || options.loopDelay || 0,
      useObserver: svg.dataset.strokeObserver === 'true' || options.useObserver || false,
      threshold: parseFloat(svg.dataset.strokeThreshold) || options.threshold || 0.3,
      observerOnce: svg.dataset.strokeObserverOnce !== 'false' && (options.observerOnce !== false),
      onComplete: options.onComplete || null,
      onStart: options.onStart || null,
      onPathComplete: options.onPathComplete || null
    };

    this.init();
  }

  init() {
    // Добавляем класс инициализации
    this.svg.classList.add('stroke-initialized');

    // Подготавливаем все пути
    this.paths.forEach((path, index) => {
      const length = this.getPathLength(path);

      // Определяем направление для каждого пути
      let pathReverse;
      if (this.options.randomDirection) {
        // Случайное направление: true или false
        pathReverse = Math.random() < 0.5;
        this.pathDirections.set(path, pathReverse);
      } else {
        // Используем общую настройку reverse
        pathReverse = this.options.reverse;
        this.pathDirections.set(path, pathReverse);
      }

      // Сохраняем оригинальные стили
      path.dataset.originalStrokeDasharray = path.style.strokeDasharray || '';
      path.dataset.originalStrokeDashoffset = path.style.strokeDashoffset || '';
      path.dataset.pathLength = length;

      // Устанавливаем начальное состояние
      path.style.strokeDasharray = length;

      // Reverse: используем отрицательное значение для рисования в обратную сторону
      // Normal: положительное значение для обычного рисования
      path.style.strokeDashoffset = pathReverse ? -length : length;
      path.style.transition = 'none';
    });

    // Если используем Observer
    if (this.options.useObserver) {
      this.setupObserver();
    } else if (this.options.autoStart) {
      this.start();
    }
  }

  getPathLength(element) {
    if (element.getTotalLength) {
      return element.getTotalLength();
    }

    // Для элементов без getTotalLength (например, rect)
    const bbox = element.getBBox();
    return (bbox.width + bbox.height) * 2;
  }

  setupObserver() {
    const options = {
      threshold: this.options.threshold,
      rootMargin: '0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Проверяем, нужно ли запускать анимацию
          if (!this.hasAnimated || !this.options.observerOnce) {
            this.start();
            this.hasAnimated = true;
          }

          // Если нужно сработать только один раз - отключаем наблюдатель
          if (this.options.observerOnce && this.hasAnimated) {
            this.observer.unobserve(this.svg);
          }
        }
      });
    }, options);

    this.observer.observe(this.svg);
  }

  start() {
    if (this.isAnimating) return;

    this.isAnimating = true;

    // Добавляем класс анимации
    this.svg.classList.add('stroke-animating');
    this.svg.classList.remove('stroke-completed');

    if (this.options.onStart) {
      this.options.onStart(this);
    }

    if (this.options.delay > 0) {
      setTimeout(() => this.animate(), this.options.delay);
    } else {
      this.animate();
    }
  }

  animate() {
    const mode = this.options.mode;

    if (mode === 'simultaneous') {
      this.animateSimultaneous();
    } else {
      this.animateSequential();
    }
  }

  animateSimultaneous() {
    this.paths.forEach((path, index) => {
      this.animatePath(path, 0);
    });

    // Устанавливаем таймер завершения
    setTimeout(() => {
      this.onAnimationComplete();
    }, this.options.duration);
  }

  animateSequential() {
    this.paths.forEach((path, index) => {
      const delay = index * this.options.stagger;
      this.animatePath(path, delay, index === this.paths.length - 1);
    });
  }

  animatePath(path, delay, isLast = false) {
    const length = parseFloat(path.dataset.pathLength);

    setTimeout(() => {
      path.style.transition = `stroke-dashoffset ${this.options.duration}ms ${this.options.easing}`;

      // Используем сохраненное направление для этого пути
      // Reverse: от -length к 0 (рисуется в обратную сторону)
      // Normal: от length к 0 (рисуется в обычную сторону)
      path.style.strokeDashoffset = 0;

      // Callback для завершения отдельного пути
      const onTransitionEnd = () => {
        if (this.options.onPathComplete) {
          this.options.onPathComplete(path, this);
        }

        if (isLast) {
          const totalDuration = this.options.mode === 'sequential'
              ? this.options.duration + (this.paths.length - 1) * this.options.stagger
              : this.options.duration;

          setTimeout(() => {
            this.onAnimationComplete();
          }, this.options.duration);
        }

        path.removeEventListener('transitionend', onTransitionEnd);
      };

      path.addEventListener('transitionend', onTransitionEnd);
    }, delay);
  }

  onAnimationComplete() {
    this.isAnimating = false;

    // Добавляем класс завершения, убираем класс анимации
    this.svg.classList.remove('stroke-animating');
    this.svg.classList.add('stroke-completed');

    if (this.options.onComplete) {
      this.options.onComplete(this);
    }

    if (this.options.loop) {
      setTimeout(() => {
        this.reset();
        this.start();
      }, this.options.loopDelay);
    }
  }

  reset() {
    this.paths.forEach(path => {
      const length = parseFloat(path.dataset.pathLength);
      path.style.transition = 'none';

      // Если randomDirection включен и нужно генерировать новые направления при каждом цикле
      if (this.options.randomDirection && this.options.loop) {
        const pathReverse = Math.random() < 0.5;
        this.pathDirections.set(path, pathReverse);
      }

      // Получаем направление для этого пути
      const pathReverse = this.pathDirections.get(path);

      // Возвращаем в начальное состояние
      // Reverse: начинаем с -length (будет рисоваться в обратную сторону)
      // Normal: начинаем с length (будет рисоваться в обычную сторону)
      path.style.strokeDashoffset = pathReverse ? -length : length;
    });

    // Форсируем reflow
    this.svg.offsetHeight;
  }

  stop() {
    this.isAnimating = false;
    this.paths.forEach(path => {
      path.style.transition = 'none';
    });
  }

  reverse() {
    this.options.reverse = !this.options.reverse;

    // Если randomDirection включен, не меняем индивидуальные направления путей
    if (!this.options.randomDirection) {
      // Обновляем направления для всех путей
      this.paths.forEach(path => {
        this.pathDirections.set(path, this.options.reverse);
      });
    }

    this.reset();
    this.start();
  }

  destroy() {
    this.stop();

    // Отключаем observer если он есть
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Удаляем классы
    this.svg.classList.remove('stroke-initialized', 'stroke-animating', 'stroke-completed');

    this.paths.forEach(path => {
      path.style.strokeDasharray = path.dataset.originalStrokeDasharray;
      path.style.strokeDashoffset = path.dataset.originalStrokeDashoffset;
      path.style.transition = '';
      delete path.dataset.pathLength;
      delete path.dataset.originalStrokeDasharray;
      delete path.dataset.originalStrokeDashoffset;
    });

    // Очищаем карту направлений
    this.pathDirections.clear();
  }
}

// Глобальный менеджер анимаций
class StrokeAnimationManager {
  constructor() {
    this.animators = new Map();
  }

  init(selector = '[data-stroke-animate="true"]') {
    const svgs = document.querySelectorAll(selector);

    svgs.forEach(svg => {
      if (!this.animators.has(svg)) {
        const animator = new StrokeAnimator(svg);
        this.animators.set(svg, animator);
      }
    });

    return this;
  }

  get(svg) {
    return this.animators.get(svg);
  }

  start(svg) {
    const animator = this.animators.get(svg);
    if (animator) animator.start();
    return this;
  }

  stop(svg) {
    const animator = this.animators.get(svg);
    if (animator) animator.stop();
    return this;
  }

  reset(svg) {
    const animator = this.animators.get(svg);
    if (animator) animator.reset();
    return this;
  }

  destroy(svg) {
    const animator = this.animators.get(svg);
    if (animator) {
      animator.destroy();
      this.animators.delete(svg);
    }
    return this;
  }

  destroyAll() {
    this.animators.forEach(animator => animator.destroy());
    this.animators.clear();
    return this;
  }
}

// Создаем глобальный экземпляр
const strokeAnimation = new StrokeAnimationManager();

// Автоинициализация при загрузке DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    strokeAnimation.init();
  });
} else {
  strokeAnimation.init();
}

// Экспортируем для использования
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { StrokeAnimator, StrokeAnimationManager, strokeAnimation };
}

// Делаем доступным глобально
if (typeof window !== 'undefined') {
  window.StrokeAnimator = StrokeAnimator;
  window.StrokeAnimationManager = StrokeAnimationManager;
  window.strokeAnimation = strokeAnimation;
}